import { useState, useMemo, memo } from "react";
import dynamic from "next/dynamic";
import Accordion from "./Accordions";
import PaginatedCards from "./PaginatedCards";
import { Restaurant } from "types/restaurant";
import { useTranslation } from "@i18n";

// Lazy-load DynamicLottiePlayer (no SSR)
const DynamicLottiePlayer = dynamic(
  () => import("@modules/common/components/dynamic-lottie-player"),
  { ssr: false, loading: () => <div className="h-64" /> }
);

// Memoized Lottie section to prevent re-renders
const LottieSection = memo(function LottieSection({ src }: { src: string }) {
  return (
    <div className="lg:w-1/2 h-[30vh] min-h-[65vw] lg:h-auto lg:!min-h-min relative !content-start lg:py-4 overflow-x-clip self-center">
      <DynamicLottiePlayer src={src} />
    </div>
  );
});

function OurClients({ restaurants }: { restaurants: Restaurant[] }) {
  const { t } = useTranslation();
  const countries = ["Egypt", "Canada", "Saudi"] as const;

  const [openCountry, setOpenCountry] = useState<string | null>("Egypt");
  const [previousCountry, setPreviousCountry] = useState<string | null>("Canada");

  // Handle country selection (updates only when changed)
  const handleCountrySelect = (country: string) => {
    if (country !== openCountry) {
      setPreviousCountry(openCountry);
      setOpenCountry(country);
    }
  };

  // Memoize transition animation key
  const currentTransition = useMemo(() => {
    return previousCountry && openCountry
      ? `${previousCountry}${openCountry.replace(" ", "")}`
      : "";
  }, [previousCountry, openCountry]);

  return (
    <div className="flex flex-col justify-center items-center w-full text-center justify-items-center px-4 lg:px-16">
      {/* Section Header */}
      <p className="text-sm lg:text-base border-[1px] border-primary text-darkGreen px-2 py-0.5 rounded-md w-fit mb-6 text-center">
        {t.ourClients.pill}
      </p>

      <h2 className="lg:text-[4vw] lg:text-6xl text-3xl">
        {t.ourClients.titleLine1} <br />
        <span className="text-primary">{t.ourClients.titleLine2}</span>
      </h2>

      {/* Main Layout */}
      <div className="w-full flex gap-4 lg:flex-row flex-col-reverse mt-16">
        {/* Accordion Section */}
        <div className="lg:w-1/2 flex flex-col gap-4 items-start justify-center">
          {countries.map((country) => {
            const countryRestaurants = restaurants.filter(
              (r) => r.country.toLowerCase() === country.toLowerCase()
            );

            if (countryRestaurants.length === 0) return null;

            return (
              <Accordion
                key={country}
                title={t.ourClients.countries[country]}
                isOpen={openCountry === country}
                onToggle={() => handleCountrySelect(country)}
              >
                <PaginatedCards restaurants={countryRestaurants} />
              </Accordion>
            );
          })}
        </div>

        {/* Lottie Animation Section */}
        <LottieSection src={currentTransition} />
      </div>
    </div>
  );
}

// Memoize entire component to reduce unnecessary re-renders
export default memo(OurClients);
